import * as Yup from 'yup'
import { TFunction } from 'i18next'
export interface ContactGoalConfig {
  id: number,
  goalId: number,
  name: string,
  status: number,
  contactGoalActionId: number,
}
export interface ContactTypeWithClassifiers {
  id: number,
  name: string,
  availableClassifiers: ContactClassifier[],
}
export interface ContactClassifier {
  id: number,
  name: string,
}

export interface ContactType {
  id: number,
  name: string,
}

export const actionKey = [
  'evalgoalactionevaluate',
  'evalgoalactioncalibrate',
  'evalgoalactionreview',
] as const

export const statusKey = [
  'editable',
  'active',
  'inactive',
] as const


export const contactTypes = [
  { id: 1, name: 'contact_goal_contact_type_call' },
  { id: 2, name: 'contact_goal_contact_type_text' },
  { id: 3, name: 'contact_goal_contact_type_email' },
  { id: 5, name: 'contact_goal_contact_type_chat' },
  { id: 6, name: 'contact_goal_contact_type_sms' },
] as const

export const contactLevel = [
  { id: 1, name: 'agent' },
  { id: 2, name: 'team' },
] as const

export const timeType = [
  { id: 1, name: 'Day' },
  { id: 2, name: 'Week' },
  { id: 3, name: 'Month' },
  { id: 4, name: 'specificDate' },
  { id: 5, name: 'dateRange' },
] as const

export const contactQueueErrorMessages = [
  { status: 400, message: 'contactQueue400Error' },
  { status: 403, message: 'contactQueue403Error' },
  { status: 500, message: 'contactQueue500Error' },
]

export const initialValues = {
  name: '',
  status: 0,
  goalQuantity: 1,
  contactGoalActionId: null,
  contactType: null,
  whoType: null,
  timeType: null,
  dueDate: null,
  effectiveStartDate: null,
  effectiveEndDate: null,
  contactStartDate: null,
  contactEndDate: null,
  instruction: '',
  classifierOperator: 'any',
  classifiers: [],
  alertOnComplete: false,
  assigneeToWhoIds: {},
}
export interface Goal {
  goalId?: number,
  name: string,
  status: number,
  goalQuantity: number,
  contactGoalActionId: number | null,
  contactType: number| null,
  whoType: number | null,
  timeType: number | null,
  dueDate: string | number | null,
  effectiveStartDate: string | number | null,
  effectiveEndDate: string | number | null,
  contactStartDate: string | number | null,
  contactEndDate: string | number | null,
  instruction: string,
  classifierOperator: string,
  classifiers: {
    id: number,
    field: string | null,
    fieldKey: string | null,
    classifierType: number,
    name: string,
    ordinal: number,
  }[],
  alertOnComplete: boolean,
  assigneeToWhoIds: {
    [key: string]: number[], // Dynamic keys with array of numbers
  },
  people?: number[],
  creatingPersonId?: number,
  percentToComplete?: number,
}

export interface Assignee {
  id: string,
  name: string,
}

/** Represents a generic entity, such as a team or an agent, id either teamId or personId */
export interface Who {
  id: number,
  textValue: string,
}

export interface AssigneeMappingField {
  rowId: number,
  assignee: Assignee,
  whoIds: Who[],
}

export const legacyGoalValidationSchema = (t: TFunction) => Yup.object().shape({
  name: Yup.string().required(t('yupIsRequired')),
  status: Yup.number().default(0),
  goalQuantity: Yup.number().min(1).max(100),
  contactGoalActionId: Yup.number().nullable().required(t('yupIsRequired')),
  contactType: Yup.number().nullable().required(t('yupIsRequired')),
  whoType: Yup.number().nullable().required(t('yupIsRequired')),
  classifierOperator: Yup.string().default('any'),
  timeType: Yup.number().nullable().required(t('yupIsRequired')),
  alertOnComplete: Yup.boolean().default(true),
  dueDate: Yup.string().nullable().when('timeType', {
    is: (timeType: number) => timeType === 4,
    then: Yup.string().required(t('yupIsRequired')),
    otherwise: Yup.string(),
  }),
  effectiveStartDate: Yup.string().nullable().when('timeType', {
    is: (timeType: number) => timeType === 5,
    then: Yup.string().required(t('yupIsRequired')),
    otherwise: Yup.string(),
  }),
  effectiveEndDate: Yup.string().nullable().when(['timeType', 'effectiveStartDate'], (timeType, effectiveStartDate, schema) => {
    if (timeType !== 5) {
      return schema
    }
    if (timeType === 5 && !effectiveStartDate) {
      return schema.required(t('yupIsRequired'))
    }
    return schema
      .required(t('yupIsRequired'))
      .test(
        'is-after-effective-start-date',
        t('invalidDateRange'),
        (value: string) => {
          return new Date(value) > new Date(effectiveStartDate)
        },
      )
  }),

  contactStartDate: Yup.string().nullable().ensure().when(['contactEndDate'], (contactEndDate, schema) => {
    if (!contactEndDate) {
      return schema
    }
    return schema
      .required(t('yupIsRequired'))
  }),

  contactEndDate: Yup.string().nullable().ensure().when(['contactStartDate'], (contactStartDate, schema) => {
    if (!contactStartDate) {
      return schema
    }
    return schema
      .required(t('yupIsRequired'))
      .test(
        'is-after-contact-start-date',
        t('invalidDateRange'),
        (value: string) => {
          return new Date(value) > new Date(contactStartDate)
        },
      )
  }),

  // classifier: Yup.number().when('contactGoalActionId', {
  //   is: (contactGoalActionId: number | null) => contactGoalActionId === null,
  //   then: Yup.number().required(t('yupIsRequired')),
  //   otherwise: Yup.number().nullable(),
  // }),
}, [['contactStartDate', 'contactEndDate']])

export const goalValidationSchema = (t: TFunction) =>  {
  const baseSchema = legacyGoalValidationSchema(t)
  return baseSchema.shape({
    assigneeToWhoIds: Yup.object()
      .transform((value, _) => value && Object.keys(value).length === 0 ? undefined : value)
      // .required(t('yupIsRequired'))
      .when(['status'], (status, schema) => {
        if (statusKey[status] === 'inactive') {
          return schema
        } else {
          return schema
            .required(t('yupIsRequired'))
            .test(
              'validateAssigneeToWhoIds',
              t('yupIsRequired'),
              (value: Record<string, number[]> | undefined) => {
                if (!value || typeof value !== 'object') {
                  return false
                }
                if (Object.keys(value).length === 0 || Object.values(value).some(whoIds => whoIds.length === 0)) {
                  return false
                }
                return true
              },
            )
        }
      }),
  })
}
